import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useUserRole } from '@/hooks/useUserRole';

export interface WelcomeMessage {
  id: string;
  title: string;
  body: string;
  target_audience: 'vendors' | 'clients' | 'both';
  style: 'info' | 'success' | 'warning' | 'important';
  start_date: string;
  end_date: string | null;
  display_mode: 'always' | 'dismissable';
  priority: number;
  status: 'draft' | 'active' | 'archived';
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

interface WelcomeMessageAnalytics {
  message_id: string;
  user_id: string;
  event_type: 'view' | 'dismiss';
}

export function useWelcomeMessages() {
  const { user } = useAuth();
  const { isClient, isVendor } = useUserRole();
  const queryClient = useQueryClient();

  // Determine the user's audience type
  const userAudience = isClient ? 'clients' : isVendor ? 'vendors' : null;

  // Fetch dismissed message IDs for the current user
  const { data: dismissedIds = [] } = useQuery({
    queryKey: ['welcome-message-dismissals', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('welcome_message_analytics')
        .select('message_id')
        .eq('user_id', user.id)
        .eq('event_type', 'dismiss');
      
      if (error) throw error;
      return data?.map(d => d.message_id) || [];
    },
    enabled: !!user?.id,
  });

  // Fetch active welcome messages for the user's role
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['welcome-messages', userAudience],
    queryFn: async () => {
      if (!userAudience) return [];

      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('welcome_messages')
        .select('*')
        .eq('status', 'active')
        .lte('start_date', now)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .or(`target_audience.eq.${userAudience},target_audience.eq.both`)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data || []) as WelcomeMessage[];
    },
    enabled: !!userAudience && !!user?.id,
  });

  // Filter out dismissed messages (for dismissable mode only)
  const activeMessages = messages.filter(msg => {
    if (msg.display_mode === 'always') return true;
    return !dismissedIds.includes(msg.id);
  });

  // Get the current message to show (highest priority undismissed)
  const currentMessage = activeMessages[0] || null;

  // Track view mutation
  const trackViewMutation = useMutation({
    mutationFn: async (messageId: string) => {
      if (!user?.id) return;
      
      const { error } = await supabase
        .from('welcome_message_analytics')
        .upsert({
          message_id: messageId,
          user_id: user.id,
          event_type: 'view' as const,
        }, {
          onConflict: 'message_id,user_id,event_type',
          ignoreDuplicates: true,
        });
      
      if (error && !error.message.includes('duplicate')) {
        console.error('Error tracking view:', error);
      }
    },
  });

  // Track dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: async (messageId: string) => {
      if (!user?.id) return;
      
      const { error } = await supabase
        .from('welcome_message_analytics')
        .upsert({
          message_id: messageId,
          user_id: user.id,
          event_type: 'dismiss' as const,
        }, {
          onConflict: 'message_id,user_id,event_type',
          ignoreDuplicates: true,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['welcome-message-dismissals'] });
    },
  });

  return {
    messages: activeMessages,
    currentMessage,
    isLoading,
    trackView: (messageId: string) => trackViewMutation.mutate(messageId),
    dismissMessage: (messageId: string) => dismissMutation.mutate(messageId),
    isDismissing: dismissMutation.isPending,
  };
}
